import React, {useState} from 'react';
import TagHistory from '@/lib/vendor/@material-ui/icons/src/History';
import type { ChangeMetrics } from '../../server/collections/revisions/collection';
import { tagGetUrl, tagGetDiscussionUrl, tagGetHistoryUrl } from '../../lib/collections/tags/helpers';
import { Link } from '../../lib/reactRouterWrapper';
import { ExpandedDate } from '../common/FormatDate';
import moment from 'moment';
import AllPostsPageTagDocDeletionItem, { DocumentDeletion } from './AllPostsPageTagDocDeletionItem';
import ChangeMetricsDisplay from "./ChangeMetricsDisplay";
import PostsItemComments from "../posts/PostsItemComments";
import AllPostsPageTagRevisionItem from "./AllPostsPageTagRevisionItem";
import { CommentByIdSuspense } from "../comments/CommentById";
import LWTooltip from "../common/LWTooltip";
import PostsItem2MetaInfo from "../posts/PostsItem2MetaInfo";
import UsersName from "../users/UsersName";
import { SuspenseWrapper } from '../common/SuspenseWrapper';
import Loading from '../vulcan-core/Loading';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';
import AnimatedExpansion from '../common/AnimatedExpansion';

export const POSTED_AT_WIDTH = 38

const styles = defineStyles('SingleLineTagUpdates', (theme: ThemeType) => ({
  root: {
    background: theme.palette.panelBackground.default,
    border: theme.palette.border.commentBorder,
    borderRadius: 3,
    marginBottom: 4,
  },
  metadata: {
    display: "flex",
    alignItems: "center",
    paddingRight: 8,
    paddingLeft: 8,
    cursor: "pointer",
  },
  title: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    cursor: "pointer",
    padding: 4,
    fontFamily: theme.typography.fontFamily,
    fontSize: 17,
    ...theme.typography.smallCaps
  },
  expandedBody: {
    marginTop: 8,
  },
  subheading: {
    fontSize: "1.17rem",
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.link.grey800,
    display: "inline-block",
    marginLeft: 8,
    marginBottom: 8,
  },
  commentBubble: {
    margin: `-5px 0px 0 11px`,
  },
  changeMetrics: {
    cursor: "pointer"
  },
  postedAt: {
    '&&': {
      cursor: "pointer",
      width: POSTED_AT_WIDTH,
      fontWeight: 300,
      fontSize: "1rem",
      color: theme.palette.text.slightlyIntense2,
      [theme.breakpoints.down('xs')]: {
        width: "auto",
      }
    }
  },
  icon: {
    height: "20px",
  },
  history: {
    display: "flex",
    alignItems: "center",
    fontSize: "1rem",
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.link.dim3,
    margin: `${-8}px 0 8px 8px`,
  },
  usernames: {
    marginRight: 16,
    maxWidth: 310,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "right",
    [theme.breakpoints.down('xs')]: {
      maxWidth: 160
    },
  },
  tagRevision: {},
}));

const SingleLineTagUpdates = ({tag, revisionIds, commentCount, commentIds, users, changeMetrics, documentDeletions, lastRevisedAt}: {
  tag: TagHistoryFragment,
  revisionIds: string[],
  commentCount?: number,
  commentIds?: string[],
  users?: UsersMinimumInfo[],
  changeMetrics: ChangeMetrics,
  documentDeletions?: DocumentDeletion[] | null,
  lastRevisedAt?: Date
}) => {
  const classes = useStyles(styles);
  const [expanded,setExpanded] = useState(false);
  documentDeletions ??= [];

  const content = <div className={classes.root} >
    <div className={classes.metadata} onClick={_ev => setExpanded(!expanded)}>

      <div className={classes.title} >
        <Link to={tagGetUrl(tag)}>{tag.name}</Link>
      </div>

      {/* Show lastRevised date with tooltip*/}
      {lastRevisedAt
        ? <LWTooltip placement="right" title={<ExpandedDate date={lastRevisedAt}/>} >
            <PostsItem2MetaInfo className={classes.postedAt}> {moment(new Date(lastRevisedAt)).fromNow()} </PostsItem2MetaInfo>
          </LWTooltip>
        : null
      }
      
      {users && users?.length > 0 && <div className={classes.usernames}>
        <PostsItem2MetaInfo>
          <UsersName user={users[0]}/>
          {users.length > 1 && users.slice(1).map(user =>
            <span key={user._id}>, <UsersName user={user}/></span>
          )}
        </PostsItem2MetaInfo>
      </div>}

      {(changeMetrics.added>0 || changeMetrics.removed>0) && <div className={classes.changeMetrics}>
        <ChangeMetricsDisplay changeMetrics={changeMetrics}/>
      </div>}
      {!!commentCount && commentCount>0 && <Link to={tagGetDiscussionUrl(tag)} className={classes.commentBubble}>
        <PostsItemComments
          small={true}
          commentCount={commentCount}
          unreadComments={false}
          newPromotedComments={false}
        />
      </Link>}
    </div>
    
    {expanded && <div className={classes.expandedBody}>
      {revisionIds.length>0 && 
        <Link
          to={tagGetHistoryUrl(tag)}
          className={classes.history}
        >
          <TagHistory className={classes.icon}  />
          <span>History</span>
        </Link>}
      
      {revisionIds.length>0 && <Link to={`revisions/w/${tag.slug}`} className={classes.subheading}>
        Edits
      </Link>}
      {revisionIds.map(revId => <div key={revId}>
        <AllPostsPageTagRevisionItem
          tag={tag}
          documentId={tag._id}
          revisionId={revId}
        />
      </div>)}

      {documentDeletions.length > 0 && <div className={classes.subheading}>
        Deletions/Restorations
      </div>}
      {documentDeletions.length > 0 && documentDeletions.map(documentDeletion => <div className={classes.tagRevision} key={documentDeletion.documentId}>
        <AllPostsPageTagDocDeletionItem
          tag={tag}
          documentDeletion={documentDeletion}
        />
      </div>)}
      
      {commentIds && commentIds.length>0 && <Link to={tagGetDiscussionUrl(tag)} className={classes.subheading}>
        Comment Threads
      </Link>}
      {commentIds && commentIds.length>0 && <SuspenseWrapper
        name="SingleLineTagUpdates_comments"
        fallback={<Loading/>}
      >
        {commentIds.map(commentId =>
          <CommentByIdSuspense
            key={commentId}
            commentId={commentId}
            nestingLevel={2}
            isChild={true}
            treeOptions={{
              tag,
            }}
            loadChildren={true}
          />
        )}
      </SuspenseWrapper>}
    </div>}
  </div>;

  return <AnimatedExpansion expanded={expanded}>{content}</AnimatedExpansion>;
}

export default SingleLineTagUpdates
